import React from 'react';
import Icon from './ui/Icon';

const GuideSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-4 pb-2 border-b-2 border-primary-500">{title}</h2>
    <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed text-justify">
      {children}
    </div>
  </div>
);

const UserGuide: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-900 p-6 sm:p-8 rounded-xl shadow-lg max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">Hướng dẫn sử dụng IVS StaySmart</h1>

      <GuideSection title="1. Cài đặt thông tin nhà trọ (Quan trọng)">
        <p>Đây là bước đầu tiên và quan trọng nhất. Thông tin bạn nhập ở đây sẽ được tự động điền vào các hợp đồng và giấy tờ, giúp bạn tiết kiệm rất nhiều thời gian.</p>
        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border dark:border-gray-600 my-4">
          <p className="font-semibold mb-2">Các trường thông tin cần điền:</p>
          <ul className="list-disc list-inside space-y-2 text-sm">
            <li>
                <span className="font-medium">Thông tin cơ sở:</span>
                <ul className="list-['-_'] list-inside ml-4 mt-1 space-y-1">
                    <li>Tên nhà trọ, địa chỉ, số lượng phòng, giá phòng mặc định.</li>
                    <li><span className="font-semibold">Cơ quan Công an địa phương:</span> Tên cơ quan công an quản lý khu vực của bạn (ví dụ: Công an Phường An Khánh, TP. Thủ Đức). Thông tin này dùng để tự động điền vào mẫu CT01.</li>
                </ul>
            </li>
            <li><span className="font-medium">Thông tin chủ sở hữu (Bên A):</span> Họ tên, ngày sinh, số CCCD của chủ nhà. Đây là thông tin sẽ xuất hiện trên hợp đồng thuê nhà.</li>
          </ul>
        </div>
        <p>Để truy cập, nhấn vào biểu tượng bánh răng <Icon name="settings" className="w-5 h-5 inline-block" /> ở góc trên bên phải hoặc ở cuối thanh điều hướng bên trái.</p>
      </GuideSection>

      <GuideSection title="2. Quản lý phòng">
        <p>Trang "Quản lý phòng" là trung tâm điều hành của bạn. Tại đây bạn có thể:</p>
        <ul className="list-disc list-inside space-y-2">
          <li>Xem toàn bộ danh sách phòng với các thông tin chi tiết: chủ phòng, số người ở, tình trạng, số điện, và tổng tiền tháng.</li>
          <li><span className="font-semibold">Nhập số điện mới:</span> Đơn giản nhập trực tiếp vào ô "Số điện mới" của từng phòng. Tiền điện và tổng cộng sẽ tự động cập nhật.</li>
          <li><span className="font-semibold">Chốt số điện:</span> Sau khi thu tiền, nhấn "Chốt số" để lưu số điện mới thành số cũ cho kỳ tiếp theo.</li>
          <li><span className="font-semibold">Quản lý toàn diện:</span> Nhấn "Quản lý" để mở một cửa sổ chi tiết, cho phép bạn:
            <ul className="list-['-_'] list-inside ml-4 mt-1">
              <li>Thêm/sửa thông tin chủ phòng (Bên B).</li>
              <li>Thêm/xóa các thành viên ở cùng.</li>
              <li>Cập nhật thông tin đăng ký cư trú.</li>
              <li>Điều chỉnh thông tin phòng như giá thuê, tình trạng, hoặc sửa lại các chỉ số điện nếu có sai sót.</li>
            </ul>
          </li>
          <li><span className="font-semibold">Xuất Hóa Đơn:</span> Nhanh chóng tạo và in hóa đơn tiền nhà chuyên nghiệp cho từng phòng.</li>
        </ul>
      </GuideSection>

      <GuideSection title="3. Quản lý khách thuê">
        <p>Trang này cung cấp một danh sách thống nhất của tất cả mọi người đang cư trú, bao gồm cả chủ phòng và thành viên.</p>
        <ul className="list-disc list-inside space-y-2">
            <li>Xem thông tin tổng quan: vai trò, số phòng, trạng thái cư trú, và ngày hết hạn hợp đồng.</li>
            <li><span className="font-semibold">In Hợp đồng & CT01:</span> Nhanh chóng tạo và in/tải về Hợp đồng thuê nhà hoặc Tờ khai CT01 cho bất kỳ ai trong danh sách.</li>
            <li>Thêm, sửa, xóa thông tin của từng người một cách dễ dàng.</li>
        </ul>
      </GuideSection>
      
       <GuideSection title="4. Quét CCCD với Gemini AI (Tính năng nâng cao)">
        <p>Để tăng tốc độ nhập liệu, ứng dụng tích hợp khả năng quét và tự động điền thông tin từ ảnh chụp Căn cước công dân.</p>
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 rounded-r-lg my-4">
            <p className="font-semibold text-yellow-800 dark:text-yellow-300">Yêu cầu:</p>
            <p>Bạn cần có <span className="font-medium">Gemini API Key</span>. Bạn có thể lấy key này miễn phí từ Google AI Studio. Sau đó, vào <span className="font-semibold">Cài đặt &gt; Cài đặt API</span> và dán key của bạn vào.</p>
        </div>
        <p>Sau khi cài đặt API Key, nút "Quét CCCD" sẽ được kích hoạt ở màn hình thêm/sửa thông tin khách thuê, cho phép bạn chụp ảnh và để AI tự điền thông tin.</p>
      </GuideSection>

      <GuideSection title="5. Sao lưu & Khôi phục">
        <p>Dữ liệu của bạn rất quan trọng. Toàn bộ thông tin được lưu trực tiếp trên trình duyệt của bạn. Để tránh mất mát dữ liệu khi chuyển máy hoặc xóa cache trình duyệt, hãy thường xuyên sao lưu.</p>
        <ul className="list-disc list-inside space-y-2">
            <li><span className="font-semibold">Sao lưu:</span> Vào mục "Sao lưu & Khôi phục", nhấn "Tải về bản Sao lưu" để lưu một file .json chứa toàn bộ dữ liệu. Hãy cất file này ở một nơi an toàn.</li>
            <li><span className="font-semibold">Khôi phục:</span> Nếu cần, bạn có thể dùng chính file .json này để khôi phục lại toàn bộ trạng thái của ứng dụng. <span className="font-bold text-red-500">Lưu ý: Thao tác này sẽ ghi đè toàn bộ dữ liệu hiện có.</span></li>
        </ul>
      </GuideSection>

    </div>
  );
};

export default UserGuide;
